import {
  DEFAULT_WINDOW_SNAP_RELEASE_THRESHOLD,
  DEFAULT_WINDOW_SNAP_THRESHOLD,
  snapWindowGeometry
} from "./window-snapping-engine.js";

const DEFAULT_MINIMUM_WIDTH = 320;
const DEFAULT_MINIMUM_HEIGHT = 240;
const SURFACE_CONTEXTS = new WeakMap();
let snapTargetSequence = 0;
const INTERACTIVE_CONTROL_SELECTOR = [
  "button",
  "a",
  "input",
  "select",
  "textarea",
  "summary",
  "[role='button']",
  "[role='menuitem']",
  "[data-window-no-drag]"
].join(", ");

export class FloatingWindowController {
  constructor({
    surface,
    windowElement,
    dragHandle,
    resizeHandles,
    onChange = null,
    onInteractionEnd = null,
    minimumWidth = DEFAULT_MINIMUM_WIDTH,
    minimumHeight = DEFAULT_MINIMUM_HEIGHT,
    snapping = {}
  }) {
    if (!(surface instanceof HTMLElement)) {
      throw new TypeError("FloatingWindowController requires a workspace surface.");
    }
    if (!(windowElement instanceof HTMLElement)) {
      throw new TypeError("FloatingWindowController requires a window element.");
    }
    if (!(dragHandle instanceof HTMLElement)) {
      throw new TypeError("FloatingWindowController requires a drag handle.");
    }

    this.surface = surface;
    this.windowElement = windowElement;
    this.dragHandle = dragHandle;
    this.resizeHandles = [...resizeHandles];
    this.onChange = onChange;
    this.onInteractionEnd = onInteractionEnd;
    this.minimumWidth = finiteOr(minimumWidth, DEFAULT_MINIMUM_WIDTH);
    this.minimumHeight = finiteOr(minimumHeight, DEFAULT_MINIMUM_HEIGHT);
    this.snapping = {
      enabled: snapping.enabled !== false,
      snapThreshold: finiteOr(
        snapping.snapThreshold,
        DEFAULT_WINDOW_SNAP_THRESHOLD
      ),
      releaseThreshold: finiteOr(
        snapping.releaseThreshold,
        DEFAULT_WINDOW_SNAP_RELEASE_THRESHOLD
      ),
      perpendicularThreshold: finiteOr(
        snapping.perpendicularThreshold,
        DEFAULT_WINDOW_SNAP_THRESHOLD
      )
    };
    this.surfaceContext = surfaceContext(surface);
    const requestedSnapTargetId = windowElement.dataset.chartInstance
      || windowElement.dataset.watchlistPanelId
      || windowElement.dataset.windowSnapId
      || `floating-window-${++snapTargetSequence}`;
    this.snapTargetId = uniqueSnapTargetId(
      this.surfaceContext,
      requestedSnapTargetId
    );
    this.windowElement.dataset.windowSnapId = this.snapTargetId;
    this.surfaceContext.controllers.add(this);
    this.snapGuides = new Map();
    this.interaction = null;
    this.state = "floating";
    this.restoreGeometry = null;
    this.abortController = new AbortController();
    const options = { signal: this.abortController.signal };

    dragHandle.addEventListener(
      "pointerdown",
      (event) => this.startInteraction(event, "move"),
      options
    );
    for (const handle of this.resizeHandles) {
      handle.addEventListener(
        "pointerdown",
        (event) => this.startInteraction(event, "resize", handle.dataset.windowResize),
        options
      );
    }
    document.addEventListener("pointermove", (event) => this.updateInteraction(event), options);
    document.addEventListener("pointerup", (event) => this.endInteraction(event), options);
    document.addEventListener(
      "pointercancel",
      (event) => this.endInteraction(event, { canceled: true }),
      options
    );
    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !this.interaction) return;
      event.preventDefault();
      this.finishInteraction({ canceled: true });
    }, options);

    this.surfaceObserver = new ResizeObserver(() => this.constrainToSurface());
    this.surfaceObserver.observe(surface);
    this.reset();
  }

  reset({ offsetX = 0, offsetY = 0 } = {}) {
    this.clearSnapGuides();
    const bounds = this.surfaceBounds();
    if (bounds.width <= 0 || bounds.height <= 0) return;
    this.state = "floating";
    this.restoreGeometry = null;
    this.windowElement.dataset.windowState = this.state;
    const geometry = defaultFloatingGeometry(bounds);
    this.applyGeometry(moveFloatingGeometry(
      geometry,
      offsetX,
      offsetY,
      bounds,
      this.minimumGeometry(bounds)
    ));
  }

  snapshot() {
    return geometryFromElements(this.surface, this.windowElement);
  }

  minimize() {
    if (this.state === "minimized") return;
    this.clearSnapGuides();
    const titleBarHeight = this.dragHandle.getBoundingClientRect().height;
    this.rememberRestoreGeometry();
    this.state = "minimized";
    this.windowElement.dataset.windowState = this.state;
    this.applyGeometry(minimizedFloatingGeometry(
      this.restoreGeometry || this.snapshot(),
      this.surfaceBounds(),
      titleBarHeight
    ));
  }

  maximize() {
    if (this.state === "maximized") return;
    this.clearSnapGuides();
    this.rememberRestoreGeometry();
    this.state = "maximized";
    this.windowElement.dataset.windowState = this.state;
    this.applyGeometry(maximizedFloatingGeometry(this.surfaceBounds()));
  }

  restore() {
    if (this.state === "floating") return;
    this.clearSnapGuides();
    const bounds = this.surfaceBounds();
    const geometry = constrainFloatingGeometry(
      this.restoreGeometry || defaultFloatingGeometry(bounds),
      bounds,
      this.minimumGeometry(bounds)
    );
    this.state = "floating";
    this.restoreGeometry = null;
    this.windowElement.dataset.windowState = this.state;
    this.applyGeometry(geometry);
  }

  startInteraction(event, type, edge = "") {
    if (event.button !== 0 || this.state !== "floating") return;
    const origin = event.target;
    if (
      type === "move"
      && origin instanceof Element
      && origin.closest(INTERACTIVE_CONTROL_SELECTOR)
    ) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();

    const target = event.currentTarget;
    if (target instanceof Element && "setPointerCapture" in target) {
      target.setPointerCapture(event.pointerId);
    }

    const geometry = this.snapshot();
    const snapTargets = this.captureSnapTargets();
    const bounds = this.surfaceBounds();
    const minimums = this.minimumGeometry(bounds);
    const initialSnapState = snapWindowGeometry({
      proposedRect: geometry,
      mode: type === "move" ? "move" : { type: "resize", edges: edge },
      workspaceBounds: bounds,
      otherRects: this.activeSnapTargets(snapTargets),
      snapThreshold: this.snapping.snapThreshold,
      releaseThreshold: this.snapping.releaseThreshold,
      perpendicularThreshold: this.snapping.perpendicularThreshold,
      constraints: {
        minWidth: minimums.width,
        minHeight: minimums.height,
        maxWidth: bounds.width,
        maxHeight: bounds.height
      },
      disabled: (
        event.altKey
        || !this.snapping.enabled
        || !this.surfaceContext.snappingEnabled
      )
    }).snapState;

    this.interaction = {
      type,
      edge,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastY: event.clientY,
      geometry,
      snapState: initialSnapState,
      snapTargets,
      captureTarget: target instanceof Element ? target : null
    };
    this.windowElement.classList.add("is-interacting");
  }

  updateInteraction(event) {
    const interaction = this.interaction;
    if (!interaction || event.pointerId !== interaction.pointerId) return;
    event.preventDefault();
    interaction.lastX = event.clientX;
    interaction.lastY = event.clientY;

    const deltaX = event.clientX - interaction.startX;
    const deltaY = event.clientY - interaction.startY;
    const bounds = this.surfaceBounds();
    const minimums = this.minimumGeometry(bounds);
    const proposedGeometry = interaction.type === "move"
      ? moveFloatingGeometry(interaction.geometry, deltaX, deltaY, bounds, minimums)
      : resizeFloatingGeometry(
        interaction.geometry,
        interaction.edge,
        deltaX,
        deltaY,
        bounds,
        minimums
      );
    const snapResult = snapWindowGeometry({
      proposedRect: proposedGeometry,
      mode: interaction.type === "move"
        ? "move"
        : { type: "resize", edges: interaction.edge },
      workspaceBounds: bounds,
      otherRects: this.activeSnapTargets(interaction.snapTargets),
      currentSnapState: interaction.snapState,
      snapThreshold: this.snapping.snapThreshold,
      releaseThreshold: this.snapping.releaseThreshold,
      perpendicularThreshold: this.snapping.perpendicularThreshold,
      constraints: {
        minWidth: minimums.width,
        minHeight: minimums.height,
        maxWidth: bounds.width,
        maxHeight: bounds.height
      },
      disabled: (
        event.altKey
        || !this.snapping.enabled
        || !this.surfaceContext.snappingEnabled
      )
    });
    interaction.snapState = snapResult.snapState;
    this.renderSnapGuides(snapResult.guides);
    this.applyGeometry(snapResult.rect);
  }

  endInteraction(event, { canceled = false } = {}) {
    if (!this.interaction || event.pointerId !== this.interaction.pointerId) return;
    this.finishInteraction({ canceled });
  }

  finishInteraction({ canceled = false } = {}) {
    const interaction = this.interaction;
    if (!interaction) return;
    const captureTarget = interaction.captureTarget;
    if (
      captureTarget
      && "hasPointerCapture" in captureTarget
      && captureTarget.hasPointerCapture(interaction.pointerId)
    ) {
      captureTarget.releasePointerCapture(interaction.pointerId);
    }
    this.interaction = null;
    this.windowElement.classList.remove("is-interacting");
    this.clearSnapGuides();
    this.onInteractionEnd?.(this.snapshot(), {
      canceled,
      type: interaction.type,
      edge: interaction.edge
    });
  }

  constrainToSurface() {
    if (this.windowElement.hidden) return;
    const bounds = this.surfaceBounds();
    if (bounds.width <= 0 || bounds.height <= 0) return;
    this.clearSnapGuides();
    if (this.state === "maximized") {
      this.applyGeometry(maximizedFloatingGeometry(bounds));
      this.rebaseInteractionAfterSurfaceChange();
      return;
    }
    if (this.state === "minimized") {
      this.applyGeometry(minimizedFloatingGeometry(
        this.restoreGeometry || this.snapshot(),
        bounds,
        this.dragHandle.getBoundingClientRect().height
      ));
      this.rebaseInteractionAfterSurfaceChange();
      return;
    }
    this.applyGeometry(constrainFloatingGeometry(
      this.snapshot(),
      bounds,
      this.minimumGeometry(bounds)
    ));
    this.rebaseInteractionAfterSurfaceChange();
  }

  applyGeometry(geometry) {
    this.windowElement.style.left = `${Math.round(geometry.x)}px`;
    this.windowElement.style.top = `${Math.round(geometry.y)}px`;
    this.windowElement.style.width = `${Math.round(geometry.width)}px`;
    this.windowElement.style.height = `${Math.round(geometry.height)}px`;
    this.onChange?.(geometry);
  }

  surfaceBounds() {
    return {
      width: this.surface.clientWidth,
      height: this.surface.clientHeight
    };
  }

  minimumGeometry(bounds) {
    return minimumGeometry(bounds, this.minimumWidth, this.minimumHeight);
  }

  rememberRestoreGeometry() {
    if (this.state === "floating") {
      this.restoreGeometry = this.snapshot();
    }
  }

  dispose() {
    this.interaction = null;
    this.abortController.abort();
    this.surfaceObserver.disconnect();
    this.surfaceContext.controllers.delete(this);
    for (const guide of this.snapGuides.values()) guide.remove();
    this.snapGuides.clear();
    this.windowElement.classList.remove("is-interacting");
  }

  captureSnapTargets() {
    const targets = [];
    for (const controller of this.surfaceContext.controllers) {
      if (controller === this || !isEligibleSnapTarget(controller, true)) continue;
      const rect = controller.snapshot();
      if (rect.width <= 0 || rect.height <= 0) continue;
      targets.push({
        id: controller.snapTargetId,
        rect,
        controller
      });
    }
    return targets;
  }

  activeSnapTargets(targets) {
    return targets.map(target => ({
      id: target.id,
      rect: target.rect,
      eligible: isEligibleSnapTarget(target.controller, false)
    }));
  }

  renderSnapGuides(guides) {
    const activeAxes = new Set();
    for (const geometry of guides) {
      const guide = this.ensureSnapGuide(geometry.axis);
      activeAxes.add(geometry.axis);
      guide.hidden = false;
      guide.dataset.snapKind = geometry.kind;
      if (geometry.axis === "x") {
        guide.style.left = `${geometry.position}px`;
        guide.style.top = `${geometry.start}px`;
        guide.style.width = "1px";
        guide.style.height = `${Math.max(1, geometry.end - geometry.start)}px`;
      } else {
        guide.style.left = `${geometry.start}px`;
        guide.style.top = `${geometry.position}px`;
        guide.style.width = `${Math.max(1, geometry.end - geometry.start)}px`;
        guide.style.height = "1px";
      }
    }
    for (const [axis, guide] of this.snapGuides) {
      if (!activeAxes.has(axis)) guide.hidden = true;
    }
  }

  ensureSnapGuide(axis) {
    let guide = this.snapGuides.get(axis);
    if (guide) return guide;
    guide = document.createElement("div");
    guide.className = "floating-window-snap-guide";
    guide.dataset.snapAxis = axis;
    guide.setAttribute("aria-hidden", "true");
    guide.hidden = true;
    this.surface.append(guide);
    this.snapGuides.set(axis, guide);
    return guide;
  }

  clearSnapGuides() {
    for (const guide of this.snapGuides.values()) guide.hidden = true;
    if (this.interaction) this.interaction.snapState = { x: null, y: null };
  }

  rebaseInteractionAfterSurfaceChange() {
    const interaction = this.interaction;
    if (!interaction) return;
    interaction.geometry = this.snapshot();
    interaction.startX = interaction.lastX;
    interaction.startY = interaction.lastY;
    interaction.snapState = { x: null, y: null };
    interaction.snapTargets = this.captureSnapTargets();
  }
}

export function setSurfaceWindowSnapping(surface, enabled) {
  const context = surfaceContext(surface);
  context.snappingEnabled = enabled !== false;
  if (surface instanceof HTMLElement) {
    surface.dataset.snapWindows = String(context.snappingEnabled);
  }
  if (!context.snappingEnabled) {
    for (const controller of context.controllers) controller.clearSnapGuides();
  }
}

export function surfaceWindowSnappingEnabled(surface) {
  return surfaceContext(surface).snappingEnabled;
}

export function defaultFloatingGeometry(bounds) {
  const compact = bounds.width < 640 || bounds.height < 440;
  const horizontalMargin = compact ? 8 : Math.max(18, bounds.width * 0.06);
  const verticalMargin = compact ? 8 : Math.max(16, bounds.height * 0.08);
  const minimums = minimumGeometry(bounds);
  const width = clamp(
    bounds.width - horizontalMargin * 2,
    minimums.width,
    bounds.width
  );
  const height = clamp(
    bounds.height - verticalMargin * 2,
    minimums.height,
    bounds.height
  );

  return {
    x: Math.max(0, (bounds.width - width) / 2),
    y: Math.max(0, (bounds.height - height) / 2),
    width,
    height
  };
}

export function maximizedFloatingGeometry(bounds) {
  return {
    x: 0,
    y: 0,
    width: Math.max(0, bounds.width),
    height: Math.max(0, bounds.height)
  };
}

export function minimizedFloatingGeometry(geometry, bounds, titleBarHeight) {
  const inset = Math.min(8, Math.max(0, Math.min(bounds.width, bounds.height) / 2));
  const availableWidth = Math.max(0, bounds.width - inset * 2);
  const width = clamp(
    Math.min(geometry.width, 300),
    Math.min(260, availableWidth),
    availableWidth
  );
  const height = clamp(titleBarHeight, 1, bounds.height);
  return {
    x: inset,
    y: Math.max(inset, bounds.height - height - inset),
    width,
    height
  };
}

export function moveFloatingGeometry(geometry, deltaX, deltaY, bounds, minimums) {
  const constrained = constrainFloatingGeometry(geometry, bounds, minimums);
  return {
    ...constrained,
    x: clamp(constrained.x + deltaX, 0, Math.max(0, bounds.width - constrained.width)),
    y: clamp(constrained.y + deltaY, 0, Math.max(0, bounds.height - constrained.height))
  };
}

export function resizeFloatingGeometry(
  geometry,
  edge,
  deltaX,
  deltaY,
  bounds,
  minimums
) {
  const constrained = constrainFloatingGeometry(geometry, bounds, minimums);
  let left = constrained.x;
  let right = constrained.x + constrained.width;
  let top = constrained.y;
  let bottom = constrained.y + constrained.height;
  const minimumWidth = Math.min(minimums.width, bounds.width);
  const minimumHeight = Math.min(minimums.height, bounds.height);

  if (edge.includes("w")) {
    left = clamp(left + deltaX, 0, right - minimumWidth);
  }
  if (edge.includes("e")) {
    right = clamp(right + deltaX, left + minimumWidth, bounds.width);
  }
  if (edge.includes("n")) {
    top = clamp(top + deltaY, 0, bottom - minimumHeight);
  }
  if (edge.includes("s")) {
    bottom = clamp(bottom + deltaY, top + minimumHeight, bounds.height);
  }

  return {
    x: left,
    y: top,
    width: right - left,
    height: bottom - top
  };
}

export function constrainFloatingGeometry(geometry, bounds, minimums) {
  const minimumWidth = Math.min(minimums.width, bounds.width);
  const minimumHeight = Math.min(minimums.height, bounds.height);
  const width = clamp(geometry.width, minimumWidth, bounds.width);
  const height = clamp(geometry.height, minimumHeight, bounds.height);
  return {
    x: clamp(geometry.x, 0, Math.max(0, bounds.width - width)),
    y: clamp(geometry.y, 0, Math.max(0, bounds.height - height)),
    width,
    height
  };
}

function geometryFromElements(surface, windowElement) {
  if (windowElement.offsetParent === surface) {
    return {
      x: windowElement.offsetLeft,
      y: windowElement.offsetTop,
      width: windowElement.offsetWidth,
      height: windowElement.offsetHeight
    };
  }

  const surfaceRect = surface.getBoundingClientRect();
  const windowRect = windowElement.getBoundingClientRect();
  return {
    x: windowRect.left - surfaceRect.left,
    y: windowRect.top - surfaceRect.top,
    width: windowRect.width,
    height: windowRect.height
  };
}

function minimumGeometry(
  bounds,
  minimumWidth = DEFAULT_MINIMUM_WIDTH,
  minimumHeight = DEFAULT_MINIMUM_HEIGHT
) {
  return {
    width: Math.min(minimumWidth, Math.max(180, bounds.width)),
    height: Math.min(minimumHeight, Math.max(180, bounds.height))
  };
}

function surfaceContext(surface) {
  let context = SURFACE_CONTEXTS.get(surface);
  if (!context) {
    context = {
      controllers: new Set(),
      snappingEnabled: true
    };
    SURFACE_CONTEXTS.set(surface, context);
  }
  return context;
}

function uniqueSnapTargetId(context, requestedId) {
  const usedIds = new Set(
    [...context.controllers].map(controller => controller.snapTargetId)
  );
  if (!usedIds.has(requestedId)) return requestedId;
  let candidate;
  do {
    candidate = `${requestedId}-${++snapTargetSequence}`;
  } while (usedIds.has(candidate));
  return candidate;
}

function isEligibleSnapTarget(controller, checkComputedStyle) {
  const element = controller?.windowElement;
  if (
    !(element instanceof HTMLElement)
    || !element.isConnected
    || element.hidden
    || controller.state !== "floating"
    || element.inert
    || element.getAttribute("aria-hidden") === "true"
    || element.dataset.windowInteractive === "false"
    || element.dataset.windowClosing === "true"
    || element.classList.contains("is-closing")
  ) {
    return false;
  }
  if (!checkComputedStyle) return true;
  const style = getComputedStyle(element);
  return (
    style.display !== "none"
    && style.visibility !== "hidden"
    && style.pointerEvents !== "none"
  );
}

function finiteOr(value, fallback) {
  return Number.isFinite(value) ? value : fallback;
}

function clamp(value, minimum, maximum) {
  if (!Number.isFinite(value)) return minimum;
  return Math.min(maximum, Math.max(minimum, value));
}
